import { useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import { Badge, Card, CardBody, Collapse } from 'reactstrap'
import Loader from '../../../../../Material/Loader'
import renderDollars from '../../../../../Functions/renderDollars'

const DRIVER_CONTEXT_QUERY = gql`
  query DriverContext($id: ID!, $documentsFirst: Int!, $documentsOrder: [String]) {
    driver(id: $id) {
      id
      name
      email
      phone
      dmvLicense
      tlcLicense
      currentCharge
      deposit
      branch { id name }
      tags { id name color }
      paymentplanSet(status: A_1, last: 1) {
        edges {
          node {
            id
            paydownAmount
            paydownOriginalBalance
            paydownStartDate
          }
        }
      }
      currentAgreement {
        id
        startDate
        car {
          id
          vin
          carModel {
            id
            make
            name
          }
        }
      }
      reservationDriver(status: "OPEN") {
        edges {
          node {
            id
            pickupDate
            pickupType
            car {
              id
              dmvPlate
              carModel {
                id
                make
                name
              }
            }
          }
        }
      }
    }
    documents: allDriverDocuments(first: $documentsFirst, orderBy: $documentsOrder, driverId: $id) {
      edges {
        node {
          id
          name
          dateAdded
          documentType {
            id
            typeName
          }
        }
      }
    }
  }
`

const DEFAULT_SECTION = 'basic'
const DOCUMENTS_LIMIT = 5

const DriverContextPanel = ({ driverId }) => {
  const [openSection, setOpenSection] = useState(DEFAULT_SECTION)

  const { data, loading, error } = useQuery(DRIVER_CONTEXT_QUERY, {
    skip: !driverId,
    variables: { id: driverId, documentsFirst: DOCUMENTS_LIMIT, documentsOrder: ['-date_added'] },
    fetchPolicy: 'cache-and-network',
  })

  const driver = data?.driver
  const paymentPlan = driver?.paymentplanSet?.edges?.[0]?.node
  const reservations = driver?.reservationDriver?.edges?.map(({ node }) => node) ?? []
  const documents = data?.documents?.edges?.map(({ node }) => node) ?? []

  const formattedSections = useMemo(() => {
    if (!driver) return []

    const paymentPlanLabel = (() => {
      if (!paymentPlan) return 'No active payment plan'
      const pieces = []
      if (paymentPlan.paydownAmount || paymentPlan.paydownOriginalBalance) {
        const amount = paymentPlan.paydownAmount ?? paymentPlan.paydownOriginalBalance
        pieces.push(`Amount: ${formatCurrency(amount)}`)
      }
      if (paymentPlan.paydownStartDate) {
        pieces.push(`Started: ${formatDate(paymentPlan.paydownStartDate)}`)
      }
      return pieces.join(' • ') || 'Active payment plan'
    })()

    const agreementCar = driver?.currentAgreement?.car
    const agreementCarModel = agreementCar?.carModel

    const sections = [
      {
        id: 'basic',
        title: 'Overview',
        icon: 'fa-id-card-o',
        content: (
          <InfoList
            items={[
              { label: 'Name', value: 
                <a href={`/driver/${driver.id}`} target="_blank" rel="noopener noreferrer">
                  {driver?.name || '--'}
                  <small className="ml-1"><i className="fa fa-external-link" aria-hidden="true" /></small>
                </a>},
              { label: 'Email', value: driver?.email },
              { label: 'Phone', value: driver?.phone },
              { label: 'Driver License', value: driver?.dmvLicense },
              { label: 'TLC License', value: driver?.tlcLicense },
              { label: 'Branch', value: driver?.branch?.name},
              { label: 'Tags', value: driver?.tags?.length > 0 ? driver.tags.map(tag => (
                <Badge key={tag.id} color={tag.color ? tag.color : "secondary"}>{tag.name}</Badge>
              )) : '--'
              }
            ]}
          />
        ),
      },
      {
        id: 'billing',
        title: 'Billing',
        icon: 'fa-credit-card',
        content: (
          <InfoList
            items={[
              { label: 'Balance', value: formatCurrency(driver?.currentCharge) },
              { label: 'Deposit', value: formatCurrency(driver?.deposit) },
              { label: 'Payment Plan', value: paymentPlanLabel },
            ]}
          />
        ),
      },
      {
        id: 'agreement',
        title: 'Current Agreement',
        icon: 'fa-car',
        content: agreementCar ? (
          <InfoList
            items={[
              {
                label: 'Vehicle',
                value: [agreementCarModel?.make, agreementCarModel?.name].filter(Boolean).join(' ') || '--',
              },
              { label: 'VIN', value: agreementCar?.vin },
              { label: 'Start Date', value: formatDate(driver?.currentAgreement?.startDate) },
            ]}
          />
        ) : (
          <EmptyState text="No active agreement" />
        ),
      },
      {
        id: 'reservations',
        title: 'Reservations',
        icon: 'fa-calendar-check-o',
        content: reservations.length > 0 ? (
          <div className="context-list">
            {reservations.map(reservation => (
              <div key={reservation.id} className="context-list-item">
                <div className="context-list-title">
                  {reservation.car?.carModel
                    ? `${reservation.car.carModel.make ?? ''} ${reservation.car.carModel.name ?? ''}`.trim()
                    : 'Reservation'}
                </div>
                <div className="context-list-meta">
                  {reservation.pickupDate && <span>Pickup: {formatDate(reservation.pickupDate)}</span>}
                  {reservation.car?.dmvPlate && <Badge color="light">Plate {reservation.car.dmvPlate}</Badge>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState text="No reservations found" />
        ),
      },
      {
        id: 'documents',
        title: `Documents${documents.length === DOCUMENTS_LIMIT ? ' (latest)' : ''}`,
        icon: 'fa-folder-open-o',
        content: documents.length > 0 ? (
          <div className="context-list">
            {documents.map(document => (
              <div key={document.id} className="context-list-item">
                <div className="context-list-title">
                  {document.documentType?.typeName || document.name || 'Document'}
                </div>
                <div className="context-list-meta">
                  {document.dateAdded && <span>Uploaded {formatDate(document.dateAdded)}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState text="No documents uploaded" />
        ),
      },
    ]

    return sections
  }, [driver, paymentPlan, reservations, documents])

  const handleToggle = sectionId => {
    setOpenSection(prev => (prev === sectionId ? '' : sectionId))
  }

  if (loading) return <Loader />
  if (error) return <p className="error-text">Unable to load driver context.</p>
  if (!driver) return <EmptyState text="Driver context unavailable" />

  return (
    <div className="driver-context-panel">
      <ul className="collapse-ul">
        {formattedSections.map(section => (
          <li key={section.id} className="collapse-li">
            <button
              className={`collapse-button ${openSection === section.id ? 'open' : ''}`}
              onClick={() => handleToggle(section.id)}
              type="button"
            >
              <span className="collapse-button-label">
                {section.icon && (
                  <i className={`fa ${section.icon} collapse-leading-icon`} aria-hidden="true" />
                )}
                {section.title}
              </span>
              <span className="collapse-button-meta">
                <i
                  className={`collapse-arrow fa ${
                    openSection === section.id ? 'fa-chevron-up' : 'fa-chevron-down'
                  }`}
                  aria-hidden="true"
                />
              </span>
            </button>
            <Collapse isOpen={openSection === section.id}>
              <Card className="driver-context-card">
                <CardBody>{section.content}</CardBody>
              </Card>
            </Collapse>
          </li>
        ))}
      </ul>
    </div>
  )
}

const InfoList = ({ items }) => (
  <dl className="context-info-list">
    {items.map(item => (
      <div key={item.label} className="context-info-row">
        <dt>{item.label}</dt>
        <dd>
          {item.value || '--'}
        </dd>
      </div>
    ))}
  </dl>
)

const EmptyState = ({ text }) => <p className="empty-text">{text}</p>

export const formatCurrency = value => {
  if (value == null || value === '') return '--'

  // GraphQL Decimal fields are serialized as strings, while some older
  // queries return numbers. Normalize both before calling renderDollars,
  // which expects a number and calls .toFixed().
  const number = Number(value)
  if (!Number.isFinite(number)) return '--'

  return renderDollars(number)
}

const formatDate = value => {
  if (!value) return '--'
  try {
    return new Date(value).toLocaleDateString()
  } catch (error) {
    return value
  }
}

export default DriverContextPanel
